import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { createRecordingPath, formatRecordingDuration, MAX_RECORDING_SECONDS, chooseRecordingMimeType, microphonePermissionHelp } from "@/lib/recording";
import ShareControls from "@/components/ShareControls";
import AccountActions from "@/components/AccountActions";
import DeleteRecordingControl from "@/components/DeleteRecordingControl";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import {
  RecordingSaveOutcomeUnknownError,
  saveUploadedRecordingVersion,
} from "@/lib/recordingLifecycle";

type VoiceRecorderProps = {
  userId: string;
  pageNumber: string;
};

type ExistingRecording = {
  id: string;
  file_path: string;
  mime_type: string;
  duration_seconds: number;
};

// This project intentionally uses a browser-only Supabase client without generated database types.
// Keep the untyped PostgREST boundary local; component state and upload payloads remain explicit.
const recordingRows = (client: NonNullable<typeof supabase>) =>
  (client as unknown as { from: (table: "recordings") => any }).from("recordings");

type RecorderMode = "idle" | "requesting" | "recording" | "paused" | "preview" | "saving";

const BUCKET = "home-twice-recordings";
const SIGNED_URL_SECONDS = 60 * 60;

export default function VoiceRecorder({ userId, pageNumber }: VoiceRecorderProps) {
  const [mode, setMode] = useState<RecorderMode>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [savedUrl, setSavedUrl] = useState<string | null>(null);
  const [savedRecording, setSavedRecording] = useState<ExistingRecording | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const activeMillisecondsRef = useRef(0);
  const activeSegmentStartedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const maxLengthNoticeRef = useRef(false);
  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    let cancelled = false;

    async function loadSavedRecording() {
      if (!supabase) return;
      try {
        const { data: row, error: queryError } = await recordingRows(supabase)
          .select("id,file_path,mime_type,duration_seconds")
          .eq("user_id", userId)
          .eq("page_number", pageNumber)
          .eq("is_current", true)
          .maybeSingle();
        if (cancelled) return;
        if (queryError) {
          setError(friendlyErrorMessage(queryError, "upload", "We couldn’t load your saved voice page. Check your connection and try again."));
          return;
        }
        if (!row) return;
        const data = row as ExistingRecording;
        const { data: signedData, error: signedError } = await supabase.storage
          .from(BUCKET)
          .createSignedUrl(data.file_path, SIGNED_URL_SECONDS);
        if (cancelled) return;
        if (signedError) {
          setError(friendlyErrorMessage(signedError, "upload", "Your recording is saved, but it couldn’t be opened. Please try again later."));
          return;
        }
        setSavedRecording(data);
        setSavedUrl(signedData.signedUrl);
        setElapsedSeconds(data.duration_seconds);
      } catch (loadFailure) {
        if (!cancelled) setError(friendlyErrorMessage(loadFailure, "upload", "We couldn’t load your saved voice page. Check your connection and try again."));
      }
    }

    void loadSavedRecording();
    return () => {
      cancelled = true;
      activeRef.current = false;
    };
  }, [pageNumber, userId]);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (!wakeLockRef.current) return;
    void wakeLockRef.current.release().catch(() => undefined);
    wakeLockRef.current = null;
  }, []);

  const requestWakeLock = useCallback(async () => {
    const wakeLock = (navigator as Navigator & { wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinel> } }).wakeLock;
    if (!wakeLock || wakeLockRef.current || document.visibilityState !== "visible") return;
    try {
      const lock = await wakeLock.request("screen");
      wakeLockRef.current = lock;
      lock.addEventListener("release", () => {
        if (wakeLockRef.current === lock) wakeLockRef.current = null;
      }, { once: true });
    } catch {
      // Wake lock is optional; recording continues without it.
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimer();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try { recorderRef.current.stop(); } catch { /* Browser is already closing the device. */ }
      }
      stopTracks();
      releaseWakeLock();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [clearTimer, previewUrl, releaseWakeLock, stopTracks]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const recorder = recorderRef.current;
      if (document.visibilityState !== "visible") {
        releaseWakeLock();
        if (recorder?.state === "recording") {
          activeMillisecondsRef.current += Date.now() - activeSegmentStartedAtRef.current;
          clearTimer();
          try {
            recorder.pause();
            setMode("paused");
            setElapsedSeconds(Math.floor(activeMillisecondsRef.current / 1000));
            setNotice("Recording paused while you were away. Your audio is saved in this preview; resume when you’re ready.");
          } catch {
            setNotice("Your browser stopped the microphone while you were away. The audio captured so far is ready to preview.");
            recorder.stop();
          }
        }
      } else if (mode === "recording") {
        void requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [clearTimer, mode, releaseWakeLock, requestWakeLock]);

  const startRecording = useCallback(async () => {
    setError(null);
    setNotice(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined") {
      setError("Audio recording isn’t available in this browser. Please try Safari or Chrome on a current phone or computer.");
      return;
    }

    setMode("requesting");
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = chooseRecordingMimeType(window.MediaRecorder, navigator.userAgent);
      const recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream);
      chunksRef.current = [];
      recorderRef.current = recorder;
      activeMillisecondsRef.current = 0;
      activeSegmentStartedAtRef.current = Date.now();
      maxLengthNoticeRef.current = false;
      setElapsedSeconds(0);
      setSaveFailed(false);

      recorder.addEventListener("dataavailable", (event: BlobEvent) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      });
      recorder.addEventListener("error", (event: Event) => {
        const recorderError = "error" in event ? (event as Event & { error?: unknown }).error : undefined;
        if (activeRef.current) setError(friendlyErrorMessage(recorderError, "upload", "Recording stopped unexpectedly. Please try again."));
      });
      recorder.addEventListener("stop", () => {
        clearTimer();
        stopTracks();
        releaseWakeLock();
        const duration = Math.max(1, Math.min(MAX_RECORDING_SECONDS, Math.round(activeMillisecondsRef.current / 1000)));
        const type = recorder.mimeType || mimeType || chunksRef.current.find((chunk) => chunk.type)?.type || "audio/mp4";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        if (!activeRef.current) return;
        if (blob.size === 0) {
          setMode("idle");
          setError("No audio was captured. Please check microphone permission and try again.");
          return;
        }
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        setElapsedSeconds(duration);
        setMode("preview");
        if (maxLengthNoticeRef.current) setNotice("The 10-minute limit was reached. Your recording is ready to preview.");
      }, { once: true });

      for (const track of stream.getTracks()) {
        track.addEventListener("ended", () => {
          if (recorder.state !== "recording" && recorder.state !== "paused") return;
          if (recorder.state === "recording") {
            activeMillisecondsRef.current += Date.now() - activeSegmentStartedAtRef.current;
          }
          setNotice("Your browser paused the microphone when the page went to the background. The audio captured so far is ready to preview.");
          recorder.stop();
        }, { once: true });
      }

      recorder.start(1000);
      setMode("recording");
      void requestWakeLock();
      timerRef.current = window.setInterval(() => {
        const totalMilliseconds = activeMillisecondsRef.current + Date.now() - activeSegmentStartedAtRef.current;
        const seconds = Math.min(MAX_RECORDING_SECONDS, Math.floor(totalMilliseconds / 1000));
        setElapsedSeconds(seconds);
        if (seconds >= MAX_RECORDING_SECONDS && recorder.state === "recording") {
          activeMillisecondsRef.current = MAX_RECORDING_SECONDS * 1000;
          maxLengthNoticeRef.current = true;
          recorder.stop();
        }
      }, 250);
    } catch (startError) {
      stream?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      releaseWakeLock();
      setMode("idle");
      const errorName = startError && typeof startError === "object" && "name" in startError
        ? String((startError as { name?: unknown }).name)
        : "";
      setError(errorName === "NotAllowedError" || errorName === "SecurityError"
        ? microphonePermissionHelp(navigator.userAgent)
        : friendlyErrorMessage(startError, "upload", "We couldn’t start the recording. Please try again."));
    }
  }, [clearTimer, previewUrl, releaseWakeLock, requestWakeLock, stopTracks]);

  const pauseRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    activeMillisecondsRef.current += Date.now() - activeSegmentStartedAtRef.current;
    recorder.pause();
    clearTimer();
    releaseWakeLock();
    setElapsedSeconds(Math.min(MAX_RECORDING_SECONDS, Math.floor(activeMillisecondsRef.current / 1000)));
    setMode("paused");
    setNotice("Recording paused. Resume when you’re ready, or finish to preview what you have.");
  }, [clearTimer, releaseWakeLock]);

  const resumeRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    activeSegmentStartedAtRef.current = Date.now();
    recorder.resume();
    setMode("recording");
    setNotice(null);
    void requestWakeLock();
    timerRef.current = window.setInterval(() => {
      const totalMilliseconds = activeMillisecondsRef.current + Date.now() - activeSegmentStartedAtRef.current;
      const seconds = Math.min(MAX_RECORDING_SECONDS, Math.floor(totalMilliseconds / 1000));
      setElapsedSeconds(seconds);
      if (seconds >= MAX_RECORDING_SECONDS && recorder.state === "recording") {
        activeMillisecondsRef.current = MAX_RECORDING_SECONDS * 1000;
        maxLengthNoticeRef.current = true;
        recorder.stop();
      }
    }, 250);
  }, [requestWakeLock]);

  const stopRecording = useCallback(() => {
    clearTimer();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      if (recorder.state === "recording") {
        activeMillisecondsRef.current += Date.now() - activeSegmentStartedAtRef.current;
      }
      recorder.stop();
    }
    releaseWakeLock();
  }, [clearTimer, releaseWakeLock]);

  const saveRecording = useCallback(async () => {
    if (!supabase || !previewBlob) {
      setError("Supabase is not connected, so this recording can’t be saved yet.");
      return;
    }
    setMode("saving");
    setError(null);
    setNotice(null);
    setSaveFailed(false);

    const mimeType = previewBlob.type || "audio/mp4";
    const uploadContentType = mimeType.split(";")[0].trim();
    const filePath = createRecordingPath(userId, pageNumber, mimeType);
    let uploadError: { message: string } | null;
    try {
      ({ error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(filePath, previewBlob, { contentType: uploadContentType, upsert: false, cacheControl: "3600" }));
    } catch (uploadFailure) {
      setMode("preview");
      setSaveFailed(true);
      setError(friendlyErrorMessage(uploadFailure, "upload", "We couldn’t upload this recording. Your audio is still here; check your connection and try again."));
      return;
    }
    if (uploadError) {
      setMode("preview");
      setSaveFailed(true);
      setError(friendlyErrorMessage(uploadError, "upload", "We couldn’t upload this recording. Your audio is still here; check your connection and tap Try again."));
      return;
    }

    const duration = Math.max(0, Math.round(elapsedSeconds));
    let inserted: ExistingRecording;
    try {
      inserted = await saveUploadedRecordingVersion(supabase, {
        p_page_number: pageNumber,
        p_file_path: filePath,
        p_mime_type: mimeType,
        p_duration_seconds: duration,
      });
    } catch (saveFailure) {
      if (!(saveFailure instanceof RecordingSaveOutcomeUnknownError)) {
        try { await supabase.storage.from(BUCKET).remove([filePath]); } catch { /* The uploaded object can be cleaned up later. */ }
      }
      setMode("preview");
      setSaveFailed(true);
      setError(friendlyErrorMessage(saveFailure, "upload", "We couldn’t save this recording. Your audio is still here; tap Try again."));
      return;
    }

    let signedData: { signedUrl: string } | null = null;
    let signedError: { message: string } | null = null;
    try {
      const result = await supabase.storage.from(BUCKET).createSignedUrl(filePath, SIGNED_URL_SECONDS);
      signedData = result.data;
      signedError = result.error;
    } catch (signFailure) {
      signedError = { message: friendlyErrorMessage(signFailure, "upload", "Your story is saved privately, but playback could not be prepared.") };
    }
    setSavedRecording({
      id: inserted.id,
      file_path: inserted.file_path,
      mime_type: inserted.mime_type,
      duration_seconds: inserted.duration_seconds,
    });
    setSavedUrl(signedError || !signedData ? null : signedData.signedUrl);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    setNotice(signedError
      ? "Your story is saved privately, but playback could not be prepared. Please reload and try again."
      : null);
    setMode("idle");
  }, [elapsedSeconds, pageNumber, previewBlob, previewUrl, savedRecording, userId]);

  const handleSavedRecordingDeleted = useCallback(() => {
    if (!supabase) return;
    setSavedRecording(null);
    setSavedUrl(null);
    setElapsedSeconds(0);
    setError(null);
    setNotice("Your recording and its share link have been deleted.");
    setMode("idle");
  }, []);

  return (
    <>
    <section className="recorder-card" aria-labelledby="recorder-title">
      <div className="recorder-heading">
        <div>
          <p className="eyebrow"><span className="eyebrow-rule" /> Your voice</p>
          <h2 id="recorder-title">Record this page</h2>
        </div>
        <span className="recording-limit">Up to 10 minutes</span>
      </div>

      {error && <p className="error-message recorder-feedback" role="alert">{error}</p>}
      {notice && <p className="setup-message recorder-feedback" role="status">{notice}</p>}

      {(previewUrl || savedUrl) && (
        <div className="audio-preview">
          <audio controls playsInline preload="metadata" src={previewUrl ?? savedUrl ?? undefined}>
            Your browser can’t play this audio recording.
          </audio>
          <p className="recording-meta">
            {previewUrl ? "Preview" : "Saved recording"} · {formatRecordingDuration(elapsedSeconds)}
          </p>
        </div>
      )}

      {mode === "recording" ? (
        <div className="recorder-actions recording-console">
          <p className="recording-status" role="status" aria-live="off">
            <span className="recording-dot" aria-hidden="true" />
            <span className="sr-only">Recording</span>
            <strong className="recording-timer">{formatRecordingDuration(elapsedSeconds)}</strong>
          </p>
          <div className="recorder-secondary-actions">
            <button type="button" className="secondary-action recorder-button" onClick={pauseRecording}>Pause</button>
            <button type="button" className="primary-action recorder-button record-done-button" onClick={stopRecording}>Done</button>
          </div>
        </div>
      ) : mode === "paused" ? (
        <div className="recorder-actions">
          <p className="recording-status" role="status">Paused · {formatRecordingDuration(elapsedSeconds)}</p>
          <div className="recorder-secondary-actions">
            <button type="button" className="primary-action recorder-button" onClick={resumeRecording}>Resume</button>
            <button type="button" className="secondary-action recorder-button" onClick={stopRecording}>Done</button>
          </div>
        </div>
      ) : mode === "requesting" ? (
        <div className="recorder-actions">
          <p className="recording-status" role="status">Waiting for microphone permission…</p>
          <button type="button" className="secondary-action recorder-button" disabled>Starting…</button>
        </div>
      ) : mode === "saving" ? (
        <div className="recorder-actions">
          <p className="recording-status" role="status">Saving privately…</p>
          <button type="button" className="secondary-action recorder-button" disabled>Saving…</button>
        </div>
      ) : mode === "preview" ? (
        <div className="recorder-actions recorder-actions-stack">
          <button type="button" className="primary-action recorder-button" onClick={() => void saveRecording()}>
            {saveFailed ? "Try again" : "Save story"}
          </button>
          <div className="recorder-secondary-actions">
            <button type="button" className="secondary-action recorder-button" onClick={() => void startRecording()}>
              Record again
            </button>
          </div>
        </div>
      ) : mode === "idle" && !savedRecording ? (
        <div className="recorder-actions recorder-actions-stack">
          <button
            type="button"
            aria-label="Tap to start"
            className="primary-action recorder-button record-start-button"
            onClick={() => void startRecording()}
          >
            <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="2" width="6" height="13" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v4m-4 0h8" />
            </svg>
          </button>
          <span className="record-start-label" aria-hidden="true">Tap to start</span>
          <p className="recorder-help">
            Tap the microphone when you’re ready. Your browser will ask to use the microphone; choose Allow. Your recording stays private to your account.
          </p>
        </div>
      ) : null}
      {mode === "idle" && savedRecording && (
        <>
        <div className="save-success" role="status" aria-live="polite">
          <p>Your story is saved</p>
        </div>
        <ShareControls recordingId={savedRecording.id} />
        <button type="button" className="secondary-action full-width record-new-version" onClick={() => void startRecording()}>
          Record a new version
        </button>
        <DeleteRecordingControl userId={userId} pageNumber={pageNumber} onDeleted={handleSavedRecordingDeleted} />
        </>
      )}
    </section>
    {(mode === "idle" || mode === "preview") && <AccountActions placement="voice" />}
    </>
  );
}
